import {
  Alert,
  Button,
  InputAdornment,
  List,
  ListItem,
  ListItemText,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { useState } from 'preact/hooks'
import { useLocation } from 'preact-iso'
import { api } from '../api/client'
import type { EventDetails } from '../api/generated/EventDetails'
import type { NewOrganizer } from '../api/generated/NewOrganizer'
import type { NewSlug } from '../api/generated/NewSlug'
import type { OrganizerInfo } from '../api/generated/OrganizerInfo'
import { useAction } from '../api/useAction'
import { useApi } from '../api/useApi'
import { useSession } from '../auth/session'
import { saveSettings, settingsOf } from '../events/Applications'
import { DuplicateDialog } from '../events/DuplicateDialog'
import { useEvent } from '../events/EventContext'
import { EventSettingsFields, type SettingsForm } from '../events/EventSettingsFields'
import { SectionPage } from '../events/SectionPage'

function Settings() {
  const { event, setEvent } = useEvent()
  const [form, setForm] = useState<SettingsForm>(() => settingsOf(event))
  const [saved, setSaved] = useState(false)
  const { error, pending, run } = useAction()
  const submit = (e: Event) => {
    e.preventDefault()
    run(async () => {
      setEvent(await saveSettings(event, { ...form, applications_open: event.applications_open }))
      setSaved(true)
    })
  }
  return (
    <Stack component="form" spacing={2} onSubmit={submit}>
      <Typography variant="h6" component="h2">
        Paramètres
      </Typography>
      <EventSettingsFields
        value={form}
        onChange={(next) => {
          setForm(next)
          setSaved(false)
        }}
      />
      {error && <Alert severity="error">{error}</Alert>}
      {saved && <Alert severity="success">Paramètres enregistrés.</Alert>}
      <Button type="submit" variant="contained" loading={pending}>
        Enregistrer
      </Button>
    </Stack>
  )
}

function Organizers({ slug }: { slug: string }) {
  const organizers = useApi<OrganizerInfo[]>(`/events/${slug}/organizers`)
  const [email, setEmail] = useState('')
  const [name, setName] = useState('')
  const { error, pending, run } = useAction()
  const add = (e: Event) => {
    e.preventDefault()
    run(async () => {
      const body: NewOrganizer = { email, name }
      organizers.setData(await api.post<OrganizerInfo[]>(`/events/${slug}/organizers`, body))
      setEmail('')
      setName('')
    })
  }
  return (
    <Stack spacing={2}>
      <Typography variant="h6" component="h2">
        Organisateurs
      </Typography>
      {organizers.error && <Alert severity="error">{organizers.error}</Alert>}
      <List dense disablePadding>
        {organizers.data?.map((organizer) => (
          <ListItem key={organizer.id} disableGutters>
            <ListItemText
              primary={organizer.display_name ?? organizer.email}
              secondary={organizer.display_name && organizer.email}
            />
          </ListItem>
        ))}
      </List>
      <Stack
        component="form"
        direction="row"
        useFlexGap
        spacing={1}
        sx={{ flexWrap: 'wrap' }}
        onSubmit={add}
      >
        <TextField
          label="Email d'un organisateur"
          type="email"
          required
          size="small"
          sx={{ flex: '1 1 200px' }}
          value={email}
          onInput={(e) => setEmail((e.target as HTMLInputElement).value)}
        />
        <TextField
          label="Prénom et nom"
          size="small"
          sx={{ flex: '1 1 160px' }}
          value={name}
          onInput={(e) => setName((e.target as HTMLInputElement).value)}
        />
        <Button type="submit" variant="outlined" loading={pending}>
          Ajouter
        </Button>
      </Stack>
      {error && <Alert severity="error">{error}</Alert>}
    </Stack>
  )
}

function Address() {
  const { event } = useEvent()
  const { route } = useLocation()
  const [slug, setSlug] = useState<string>()
  const { error, pending, run } = useAction()
  const save = (e: Event) => {
    e.preventDefault()
    if (slug === undefined) return
    run(async () => {
      const body: NewSlug = { slug }
      const renamed = await api.post<EventDetails>(`/events/${event.slug}/slug`, body)
      route(`/events/${renamed.slug}/settings`, true)
    })
  }
  return (
    <Stack spacing={1.5}>
      <Typography variant="h6" component="h2">
        Adresse
      </Typography>
      {slug === undefined ? (
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography sx={{ flex: 1, minWidth: 0, overflowWrap: 'anywhere' }}>
            {window.location.host}/events/{event.slug}
          </Typography>
          <Button onClick={() => setSlug(event.slug)}>Modifier</Button>
        </Stack>
      ) : (
        <Stack component="form" spacing={1.5} onSubmit={save}>
          <TextField
            label="Adresse"
            required
            autoFocus
            value={slug}
            onInput={(e) => setSlug((e.target as HTMLInputElement).value)}
            helperText="Lettres minuscules, chiffres et tirets"
            slotProps={{
              input: { startAdornment: <InputAdornment position="start">/events/</InputAdornment> },
            }}
          />
          <Alert severity="warning">
            Les liens déjà partagés avec l'ancienne adresse (lien d'inscription, QR code, emails et
            notifications envoyés) ne fonctionneront plus.
          </Alert>
          {error && <Alert severity="error">{error}</Alert>}
          <Stack direction="row" spacing={1}>
            <Button
              type="submit"
              variant="contained"
              loading={pending}
              disabled={slug === event.slug}
            >
              Changer l'adresse
            </Button>
            <Button onClick={() => setSlug(undefined)}>Annuler</Button>
          </Stack>
        </Stack>
      )}
    </Stack>
  )
}

function Duplicate() {
  const { event } = useEvent()
  const [open, setOpen] = useState(false)
  return (
    <Stack spacing={1} sx={{ alignItems: 'flex-start' }}>
      <Typography variant="h6" component="h2">
        Dupliquer
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Pour une prochaine édition : même planning, sans les bénévoles.
      </Typography>
      <Button variant="outlined" onClick={() => setOpen(true)}>
        Dupliquer l'événement
      </Button>
      {open && <DuplicateDialog event={event} onClose={() => setOpen(false)} />}
    </Stack>
  )
}

export function SettingsPage() {
  const { event } = useEvent()
  const { me } = useSession()
  return (
    <SectionPage title="Réglages" maxWidth="sm">
      <Stack spacing={3}>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Settings />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Organizers slug={event.slug} />
        </Paper>
        <Paper variant="outlined" sx={{ p: 2 }}>
          <Address />
        </Paper>
        {me?.can_create_events && (
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Duplicate />
          </Paper>
        )}
      </Stack>
    </SectionPage>
  )
}
